import { Alert } from '@dispatch/ui';
import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { apiRequest, errorMessage } from './api.js';
import type { AuthenticatedResponse, LoginResponse, TotpEnrolmentResponse } from './types.js';

type AuthStep =
  | { name: 'login' }
  | { name: 'mfa'; challengeToken: string; methods: readonly ('totp' | 'recovery_code')[] }
  | { name: 'enrol'; enrollmentToken: string; enrolment: TotpEnrolmentResponse }
  | { name: 'recovery'; codes: readonly string[] }
  | { name: 'forgot' }
  | { name: 'forgot-sent' }
  | { name: 'reset-validating'; token: string }
  | { name: 'reset'; token: string }
  | { name: 'reset-invalid'; message: string }
  | { name: 'reset-complete' };

export function readCsrfCookie(): string | undefined {
  const values = document.cookie
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const separator = item.indexOf('=');
      return separator === -1
        ? { name: item, value: '' }
        : { name: item.slice(0, separator), value: item.slice(separator + 1) };
    });
  const exact = values.find((item) => item.name === 'dispatch_session_csrf');
  const candidate = exact ?? values.find((item) => item.name.endsWith('_csrf'));
  return candidate ? decodeURIComponent(candidate.value) : undefined;
}

export function AuthGate({ onAuthenticated }: { onAuthenticated: (csrfToken: string) => void }) {
  const resetToken = useMemo(() => new URLSearchParams(window.location.search).get('reset'), []);
  const [step, setStep] = useState<AuthStep>(
    resetToken ? { name: 'reset-validating', token: resetToken } : { name: 'login' },
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [method, setMethod] = useState<'totp' | 'recovery_code'>('totp');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const confirmationEntered = confirmPassword.length > 0;
  const passwordsMatch = confirmationEntered && password === confirmPassword;
  const passwordReady = password.length >= 12 && password.length <= 128 && passwordsMatch;

  useEffect(() => {
    if (step.name !== 'reset-validating') return;

    const controller = new AbortController();
    const token = step.token;

    void (async () => {
      try {
        await apiRequest('/api/v1/auth/password/reset/validate', {
          method: 'POST',
          body: { token },
          signal: controller.signal,
        });
        setStep((current) =>
          current.name === 'reset-validating' && current.token === token
            ? { name: 'reset', token }
            : current,
        );
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setStep({ name: 'reset-invalid', message: errorMessage(caught) });
      }
    })();

    return () => controller.abort();
  }, [step]);

  async function submitLogin(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const result = await apiRequest<LoginResponse>('/api/v1/auth/login', {
        method: 'POST',
        body: { email, password, sessionType: 'office' },
      });
      setPassword('');
      if (result.status === 'mfa_required') {
        const preferred = result.methods.includes('totp') ? 'totp' : 'recovery_code';
        setMethod(preferred);
        setCode('');
        setStep({ name: 'mfa', challengeToken: result.challengeToken, methods: result.methods });
        return;
      }
      const enrolment = await apiRequest<TotpEnrolmentResponse>('/api/v1/auth/mfa/totp/enrol', {
        method: 'POST',
        body: { enrollmentToken: result.enrollmentToken, label: 'Office authenticator' },
      });
      setCode('');
      setStep({ name: 'enrol', enrollmentToken: result.enrollmentToken, enrolment });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step.name !== 'mfa') return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await apiRequest<AuthenticatedResponse>('/api/v1/auth/mfa/verify', {
        method: 'POST',
        body: { challengeToken: step.challengeToken, method, code },
      });
      onAuthenticated(result.csrfToken);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitEnrolment(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step.name !== 'enrol') return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await apiRequest<AuthenticatedResponse>(
        '/api/v1/auth/mfa/totp/verify-enrolment',
        {
          method: 'POST',
          body: {
            enrollmentToken: step.enrollmentToken,
            methodId: step.enrolment.methodId,
            code,
          },
        },
      );
      if (result.recoveryCodes && result.recoveryCodes.length > 0) {
        setStep({ name: 'recovery', codes: result.recoveryCodes });
        setCode('');
      } else {
        onAuthenticated(result.csrfToken);
      }
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitForgot(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await apiRequest('/api/v1/auth/password/forgot', {
        method: 'POST',
        body: { email },
      });
      setStep({ name: 'forgot-sent' });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitReset(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step.name !== 'reset') return;
    if (!passwordReady) {
      setError(
        password.length < 12 || password.length > 128
          ? 'Password must contain 12 to 128 characters.'
          : 'Passwords do not match.',
      );
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await apiRequest('/api/v1/auth/password/reset', {
        method: 'POST',
        body: { token: step.token, password },
      });
      window.history.replaceState({}, '', window.location.pathname);
      setPassword('');
      setConfirmPassword('');
      setStep({ name: 'reset-complete' });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-layout">
      <section className="panel auth-card" aria-labelledby="auth-heading">
        {error ? <Alert title="Unable to continue">{error}</Alert> : null}

        {step.name === 'login' ? (
          <>
            <h2 id="auth-heading">Sign in</h2>
            <p className="lead">Use your global Dispatch Platform account.</p>
            <form className="form-grid" onSubmit={(event) => void submitLogin(event)}>
              <div className="form-field">
                <label htmlFor="login-email">Email address</label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="login-password">Password</label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              <div className="actions">
                <button type="submit" disabled={busy}>
                  {busy ? 'Signing in…' : 'Continue'}
                </button>
                <button type="button" className="plain" onClick={() => setStep({ name: 'forgot' })}>
                  Forgotten password
                </button>
              </div>
            </form>
          </>
        ) : null}

        {step.name === 'mfa' ? (
          <>
            <h2 id="auth-heading">Two-factor authentication</h2>
            <p className="lead">Enter a current authenticator code or a recovery code.</p>
            <form className="form-grid" onSubmit={(event) => void submitMfa(event)}>
              {step.methods.length > 1 ? (
                <div className="form-field">
                  <label htmlFor="mfa-method">Verification method</label>
                  <select
                    id="mfa-method"
                    value={method}
                    onChange={(event) => {
                      setMethod(event.target.value as 'totp' | 'recovery_code');
                      setCode('');
                    }}
                  >
                    {step.methods.includes('totp') ? (
                      <option value="totp">Authenticator app</option>
                    ) : null}
                    {step.methods.includes('recovery_code') ? (
                      <option value="recovery_code">Recovery code</option>
                    ) : null}
                  </select>
                </div>
              ) : null}
              <div className="form-field">
                <label htmlFor="mfa-code">
                  {method === 'totp' ? 'Six-digit code' : 'Recovery code'}
                </label>
                <input
                  id="mfa-code"
                  inputMode={method === 'totp' ? 'numeric' : 'text'}
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>
              <div className="actions">
                <button type="submit" disabled={busy}>
                  {busy ? 'Verifying…' : 'Verify and sign in'}
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setStep({ name: 'login' })}
                >
                  Back
                </button>
              </div>
            </form>
          </>
        ) : null}

        {step.name === 'enrol' ? (
          <>
            <h2 id="auth-heading">Set up two-factor authentication</h2>
            <p className="lead">
              Add the account to an authenticator application, then enter the current six-digit
              code.
            </p>
            <div className="form-grid">
              <div>
                <strong>Manual setup key</strong>
                <div className="code-block">{step.enrolment.secret}</div>
              </div>
              <details>
                <summary>Authenticator URI</summary>
                <div className="code-block">{step.enrolment.uri}</div>
              </details>
            </div>
            <form className="form-grid" onSubmit={(event) => void submitEnrolment(event)}>
              <div className="form-field">
                <label htmlFor="enrol-code">Six-digit code</label>
                <input
                  id="enrol-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>
              <button type="submit" disabled={busy}>
                {busy ? 'Verifying…' : 'Complete setup'}
              </button>
            </form>
          </>
        ) : null}

        {step.name === 'recovery' ? (
          <>
            <h2 id="auth-heading">Save your recovery codes</h2>
            <Alert tone="warning" title="Store these now">
              Each code can be used once. They are not shown again.
            </Alert>
            <ul className="recovery-codes">
              {step.codes.map((recoveryCode) => (
                <li key={recoveryCode}>{recoveryCode}</li>
              ))}
            </ul>
            <div className="actions">
              <button
                type="button"
                onClick={() => {
                  const csrf = readCsrfCookie();
                  if (csrf) onAuthenticated(csrf);
                  else
                    setError(
                      'The session was created but the CSRF cookie is unavailable. Sign in again.',
                    );
                }}
              >
                I have saved these codes
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => void navigator.clipboard.writeText(step.codes.join('\n'))}
              >
                Copy codes
              </button>
            </div>
          </>
        ) : null}

        {step.name === 'forgot' ? (
          <>
            <h2 id="auth-heading">Reset password</h2>
            <p className="lead">Enter the email address for the account.</p>
            <form className="form-grid" onSubmit={(event) => void submitForgot(event)}>
              <div className="form-field">
                <label htmlFor="forgot-email">Email address</label>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className="actions">
                <button type="submit" disabled={busy}>
                  {busy ? 'Submitting…' : 'Send reset instructions'}
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setStep({ name: 'login' })}
                >
                  Back
                </button>
              </div>
            </form>
          </>
        ) : null}

        {step.name === 'forgot-sent' ? (
          <>
            <h2 id="auth-heading">Request accepted</h2>
            <p>
              If the account exists, reset instructions will be sent through the configured
              identity-email channel.
            </p>
            <button type="button" onClick={() => setStep({ name: 'login' })}>
              Return to sign in
            </button>
          </>
        ) : null}

        {step.name === 'reset-validating' ? (
          <>
            <h2 id="auth-heading">Checking reset link</h2>
            <p className="lead">Please wait while this password reset link is verified.</p>
          </>
        ) : null}

        {step.name === 'reset-invalid' ? (
          <>
            <h2 id="auth-heading">Reset link unavailable</h2>
            <Alert title="Unable to reset password">{step.message}</Alert>
            <p>This link may have expired, already been used or been replaced by a newer link.</p>
            <button
              type="button"
              onClick={() => {
                window.history.replaceState({}, '', window.location.pathname);
                setPassword('');
                setConfirmPassword('');
                setError(undefined);
                setStep({ name: 'login' });
              }}
            >
              Return to sign in
            </button>
          </>
        ) : null}

        {step.name === 'reset' ? (
          <>
            <h2 id="auth-heading">Choose a new password</h2>
            <p className="lead" id="password-requirements">
              Use 12 to 128 characters.
            </p>
            <form className="form-grid" onSubmit={(event) => void submitReset(event)}>
              <div className="form-field">
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  required
                  aria-describedby="password-requirements"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setError(undefined);
                  }}
                />
              </div>
              <div className="form-field">
                <label htmlFor="confirm-password">Confirm password</label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  required
                  aria-describedby={
                    confirmationEntered ? 'password-confirmation-status' : undefined
                  }
                  aria-invalid={confirmationEntered && !passwordsMatch}
                  value={confirmPassword}
                  onChange={(event) => {
                    setConfirmPassword(event.target.value);
                    setError(undefined);
                  }}
                />
                {confirmationEntered ? (
                  <p
                    id="password-confirmation-status"
                    className="help-text"
                    role={passwordsMatch ? 'status' : 'alert'}
                    aria-live="polite"
                  >
                    {passwordsMatch ? 'Passwords match.' : 'Passwords do not match.'}
                  </p>
                ) : null}
              </div>
              <button type="submit" disabled={busy || !passwordReady}>
                {busy ? 'Resetting…' : 'Reset password'}
              </button>
            </form>
          </>
        ) : null}

        {step.name === 'reset-complete' ? (
          <>
            <h2 id="auth-heading">Password reset</h2>
            <Alert tone="success">
              Your password has been changed. Sign in with the new password.
            </Alert>
            <button type="button" onClick={() => setStep({ name: 'login' })}>
              Sign in
            </button>
          </>
        ) : null}
      </section>
    </div>
  );
}
